import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useBanner } from '../contexts/BannerContext';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import logo from '../assets/logo.png';

const Header = () => {
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const { showBanner, setShowBanner, isAtTop } = useBanner();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { lang } = useParams();
  const location = useLocation();

  // Check if we're on the home page
  const isHomePage = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-hide banner after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBanner(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [setShowBanner]);

  const handleNavigation = (sectionId) => {
    setIsMobileMenuOpen(false);

    if (isHomePage) {
      // If on home page, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If not on home page, navigate to home first
      navigate(`/${lang || 'en'}`);
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleLogoClick = () => {
    navigate(`/${lang || 'en'}`);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleContactClick = () => {
    if (isHomePage) {
      // If on home page, scroll to contact section
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If not on home page, navigate to contact page
      navigate(`/${lang || 'en'}/contact`);
    }
    setIsMobileMenuOpen(false);
  };

  const bannerText = {
    en: "🇸🇦 One of the Best IT Solutions in Saudi Arabia - 2026",
    ar: "🇸🇦 من أفضل حلول تقنية المعلومات في المملكة العربية السعودية - 2026"
  };

  return (
    <>
      {/* Top Banner */}
      <div
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 ${showBanner ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
          }`}
      >
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex-1 flex items-center justify-center gap-2">
              <span className="text-sm sm:text-base font-semibold text-center">
                {bannerText[language]}
              </span>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="p-1 hover:bg-white/20 rounded transition-colors ml-2"
              aria-label="Close banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Header */}
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${showBanner ? 'top-10' : 'top-0'
          } ${isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <button
                onClick={handleLogoClick}
                className="flex items-center text-2xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 px-3 py-1 rounded-lg shadow-sm"
                style={{ backgroundColor: "#ffffff" }}
              >
                {isRTL ? (
                  <>
                    <span>didoo</span>
                    <img src={logo} alt="Adidoo Logo" className="w-8 h-8" />
                  </>
                ) : (
                  <>
                    <img src={logo} alt="Adidoo Logo" className="w-8 h-8" />
                    <span>didoo</span>
                  </>
                )}
              </button>

            </div>


            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {[
                { key: 'home', id: 'hero' },
                { key: 'services', id: 'services' },
                { key: 'offers', id: 'offers' },
                { key: 'portfolio', id: 'portfolio' },
                { key: 'team', id: 'team' },
                { key: 'blog', id: 'blog' },
                { key: 'contact', id: 'contact' },
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleNavigation(item.id)}
                  className="px-4 py-2 text-gray-700 hover:text-teal-600 font-medium transition-colors duration-200 rounded-lg hover:bg-teal-50"
                >
                  {t.nav[item.key]}
                </button>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              {/* Language Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="flex items-center gap-2 hover:bg-teal-50 hover:text-teal-600"
              >
                <Globe className="w-4 h-4" />
                <span className="font-medium">{language === 'en' ? 'AR' : 'EN'}</span>
              </Button>

              {/* Get Started Button */}
              <Button
                onClick={handleContactClick}
                className="hidden md:flex bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {t.nav.getStarted}
              </Button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="px-4 py-6 space-y-3">
              {[
                { key: 'home', id: 'hero' },
                { key: 'services', id: 'services' },
                { key: 'offers', id: 'offers' },
                { key: 'portfolio', id: 'portfolio' },
                { key: 'team', id: 'team' },
                { key: 'blog', id: 'blog' },
                { key: 'contact', id: 'contact' },
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleNavigation(item.id)}
                  className="block w-full text-left px-4 py-3 text-gray-700 hover:text-teal-600 hover:bg-teal-50 rounded-lg font-medium transition-colors"
                >
                  {t.nav[item.key]}
                </button>
              ))}
              <Button
                onClick={handleContactClick}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                {t.nav.getStarted}
              </Button>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;